// App-level router: hash-route for /demo, otherwise mount the shell the user
// chose. The chat state machine + SSE plumbing live in useChatState() so all
// shells consume the same wiring; shells differ only in chrome and layout.
import { useCallback, useEffect, useState } from 'react';
import { Header } from './components/Header';
import { Demo } from './pages/Demo';
import { useChatState } from './useChatState';
import { isShell } from './shells/types';
import type { Shell, ShellProps } from './shells/types';
import { ClassicShell } from './shells/ClassicShell';
import { AtlasShell } from './shells/AtlasShell';
import { WorkspaceShell } from './shells/WorkspaceShell';
import { DashboardShell } from './shells/DashboardShell';

const SHELL_STORAGE_KEY = 'hrsa.shell';

function loadShell(): Shell {
  if (typeof window === 'undefined') return 'classic';
  try {
    const raw = window.localStorage.getItem(SHELL_STORAGE_KEY);
    return isShell(raw) ? raw : 'classic';
  } catch {
    // localStorage unavailable (private mode in some browsers, etc.) — fall
    // through to default. Don't surface this to the user.
    return 'classic';
  }
}

function useHashRoute(): string {
  const [hash, setHash] = useState(() => (typeof window !== 'undefined' ? window.location.hash : ''));
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}

export function App() {
  const route = useHashRoute();
  if (route === '#/demo' || route === '#demo') return <Demo />;
  return <ChatApp />;
}

function ChatApp() {
  const [shell, setShellState] = useState<Shell>(() => loadShell());
  const setShell = useCallback((s: Shell) => {
    setShellState(s);
    try {
      window.localStorage.setItem(SHELL_STORAGE_KEY, s);
    } catch {
      // localStorage write may fail in some browser modes — choice still
      // persists for the session via React state, just not across reload.
    }
  }, []);

  const { current, priorTurns, submit, askAnother } = useChatState();

  const shellProps: ShellProps = {
    current,
    priorTurns,
    submit,
    askAnother,
    shell,
    onShellChange: setShell,
  };

  return (
    <div className="app">
      <Header
        showAskAnother={Boolean(current)}
        onAskAnother={askAnother}
        shell={shell}
        onShellChange={setShell}
      />
      {renderShell(shell, shellProps)}
    </div>
  );
}

function renderShell(shell: Shell, props: ShellProps) {
  switch (shell) {
    case 'classic':   return <ClassicShell {...props} />;
    case 'atlas':     return <AtlasShell {...props} />;
    case 'workspace': return <WorkspaceShell {...props} />;
    case 'dashboard': return <DashboardShell {...props} />;
  }
}
